import { Select } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import "./animatedSelectStyles.css";

function AnimatedCustomSelect({
    label,
    options,
    required,
    handleSelectChange,
    isSelected,
    value,
    handleSelectOnSearch,
    handleSelectOnSelect,
    showSearch,
    allowClear,
    disabled,
    placeholder,
    style,
    source,
    autoFocus,
    onClear,
    onKeyDown,
    fromTagSelect,
    keepOpenOnClick = false,
    open: controlledOpen,
    onDropdownVisibleChange,
}) {
    const [isFocused, setIsFocused] = useState(false);
    const [internalOpen, setInternalOpen] = useState(false);
    const containerRef = useRef(null);
    const selectRef = useRef(null);
    const isInsideClickRef = useRef(false);

    useEffect(() => {
        if (!keepOpenOnClick) return;

        const handleStart = (e) => {
            const elem = e.target instanceof Element ? e.target : e.target?.parentElement;
            const isInsideField = containerRef.current &&
                containerRef.current.contains(elem) &&
                !elem?.closest?.('.ant-select-dropdown');
            isInsideClickRef.current = Boolean(isInsideField);
        };

        const handleEnd = () => {
            setTimeout(() => {
                isInsideClickRef.current = false;
            }, 50);
        };

        document.addEventListener('mousedown', handleStart, true);
        document.addEventListener('touchstart', handleStart, true);
        document.addEventListener('mouseup', handleEnd, true);
        document.addEventListener('touchend', handleEnd, true);
        return () => {
            document.removeEventListener('mousedown', handleStart, true);
            document.removeEventListener('touchstart', handleStart, true);
            document.removeEventListener('mouseup', handleEnd, true);
            document.removeEventListener('touchend', handleEnd, true);
        };
    }, [keepOpenOnClick]);

    useEffect(() => {
        if (keepOpenOnClick) return;
        setIsFocused(isSelected);
    }, [value, isSelected, keepOpenOnClick]);

    useEffect(() => {
        if (!keepOpenOnClick) return;

        if (!value && !internalOpen) {
            setIsFocused(Boolean(isSelected));
        }
    }, [value, internalOpen, isSelected, keepOpenOnClick]);

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        if (!keepOpenOnClick) {
            setIsFocused(isSelected);
            return;
        }
        if (!internalOpen && !isInsideClickRef.current) {
            setIsFocused(Boolean(value || isSelected));
        }
    };

    const handleDropdownVisibleChangeInternal = (nextOpen) => {
        if (!keepOpenOnClick) {
            onDropdownVisibleChange?.(nextOpen);
            return;
        }

        if (nextOpen) {
            setInternalOpen(true);
            setIsFocused(true);
            onDropdownVisibleChange?.(true);
        } else {
            if (isInsideClickRef.current) {
                // User clicked on the field while already open -> keep dropdown open
                setInternalOpen(true);
                setIsFocused(true);
                onDropdownVisibleChange?.(true);
            } else {
                setInternalOpen(false);
                setIsFocused(Boolean(value || isSelected));
                onDropdownVisibleChange?.(false);
            }
        }
        isInsideClickRef.current = false;
    };

    const handleChange = (val, opt) => {
        if (keepOpenOnClick) {
            setInternalOpen(false);
            setIsFocused(true);
        }
        handleSelectChange?.(val, opt);
    };

    const handleSelect = (val, opt) => {
        if (keepOpenOnClick) {
            setInternalOpen(false);
            setIsFocused(true);
        }
        handleSelectOnSelect?.(val, opt);
    };

    const handleClear = () => {
        if (keepOpenOnClick) {
            setInternalOpen(false);
        } else {
            selectRef.current?.blur();
        }
        setIsFocused(false);
        onClear?.();
    };

    const selectOpenProp = keepOpenOnClick
        ? (disabled ? false : (controlledOpen !== undefined ? controlledOpen : internalOpen))
        : (controlledOpen !== undefined ? controlledOpen : undefined);

    return (
        <div
            ref={containerRef}
            className={`custom-select-container ${((keepOpenOnClick && internalOpen) || isFocused || autoFocus || isSelected || value) ? 'focused' : ''} ${disabled ? 'disabled-select-container' : ''}`}
            style={style}
        >
            <Select
                ref={selectRef}
                className='custom-select no-border-select'
                open={selectOpenProp}
                onDropdownVisibleChange={keepOpenOnClick ? handleDropdownVisibleChangeInternal : onDropdownVisibleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                style={{ width: "100%", cursor: disabled ? 'not-allowed' : 'pointer' }}
                onChange={handleChange}
                value={fromTagSelect ? null : value ? value : undefined}
                onSearch={handleSelectOnSearch}
                onSelect={handleSelect}
                showSearch={showSearch ? true : false}
                allowClear={allowClear}
                onClear={handleClear}
                disabled={disabled}
                placeholder={value ? undefined : placeholder ? placeholder : ""}
                onKeyDown={onKeyDown}
            >
                {Array.isArray(options) && options.map(option => (
                    <Select.Option key={option?.id} id={option?.id} value={source == 'CRM' ? option?.id : option?.label ? option?.label : option?.name ? option?.name : option?.id}>
                        {option?.label ? option?.label : option?.value || option?.name}
                    </Select.Option>
                ))}
                {value && !Array.isArray(options) && (
                    <Select.Option key={-1} value={value}>Create {value}</Select.Option>
                )}
            </Select>
            <label className={`custom-select-label`} style={{ pointerEvents: 'none' }}>
                {label}{required && <span style={{ color: 'red' }}> *</span>}
            </label>
        </div>
    );
}

export default AnimatedCustomSelect;